import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { listInvestors, type Investor } from '@/api/investors';

export const INVESTORS_KEY = ['investors'] as const;

export function useInvestors(includeInactive = false) {
  return useQuery({
    queryKey: [...INVESTORS_KEY, { includeInactive }],
    queryFn: () => listInvestors(includeInactive),
  });
}

export function useInvestorNames(): string[] {
  const q = useInvestors();
  return (q.data ?? []).map((i: Investor) => i.name);
}

interface CreateInput {
  name: string;
  display_order?: number;
  active?: boolean;
  notes?: string | null;
}
interface UpdateInput {
  name?: string;
  display_order?: number;
  active?: boolean;
  notes?: string | null;
}

export function useCreateInvestor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInput) =>
      apiFetch<Investor>('/investors-admin', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: INVESTORS_KEY }),
  });
}

export function useUpdateInvestor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateInput }) =>
      apiFetch<Investor>(`/investors-admin/${id}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: INVESTORS_KEY }),
  });
}

export function useDeleteInvestor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ status: 'deleted' }>(`/investors-admin/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: INVESTORS_KEY }),
  });
}
